"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const MENU_WIDTH = 180;

export default function CourseCardMenu({
    entityId,
    entityKind = "buyer",
    isArchived = false,
    onArchiveChange,
    size = "md",
    className = "",
}) {
    const t = useTranslations("CourseSection");
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    const updateCoords = () => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        setCoords({
            top: rect.bottom + 4,
            left: Math.max(8, rect.right - MENU_WIDTH),
        });
    };

    useLayoutEffect(() => {
        if (!isOpen) return;
        updateCoords();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const onScroll = () => updateCoords();
        const onResize = () => updateCoords();
        const onDocClick = (e) => {
            const inMenu = menuRef.current && menuRef.current.contains(e.target);
            const inButton =
                buttonRef.current && buttonRef.current.contains(e.target);
            if (!inMenu && !inButton) setIsOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setIsOpen(false);
        };

        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [isOpen]);

    const stop = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const toggleArchive = async (e) => {
        stop(e);
        if (isLoading || !entityId) return;

        const endpoint =
            entityKind === "student"
                ? "/api/user/put/archive-accelerator"
                : "/api/user/put/archive-course";
        const body =
            entityKind === "student"
                ? { studentId: entityId, archived: !isArchived }
                : { buyerId: entityId, archived: !isArchived };

        setIsLoading(true);
        try {
            const response = await fetch(endpoint, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            const data = await response.json();

            if (!response.ok) {
                toast.error(data?.message || t("archive_error"));
            } else {
                toast.success(
                    !isArchived ? t("archive_success") : t("unarchive_success"),
                );
                setIsOpen(false);
                if (onArchiveChange) {
                    onArchiveChange({ entityId, entityKind, archived: !isArchived });
                }
            }
        } catch (err) {
            toast.error(t("archive_error"));
        } finally {
            setIsLoading(false);
        }
    };

    const buttonSize = size === "sm" ? "w-7 h-7" : "w-8 h-8";
    const iconSizeClass = size === "sm" ? "text-[16px]" : "text-[18px]";

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                aria-label={t("open_menu")}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onPointerDown={stop}
                onClick={(e) => {
                    stop(e);
                    setIsOpen((v) => !v);
                }}
                className={`${buttonSize} flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-900 dark:text-grayDark-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors ${className}`}
            >
                <SolarIcon name="menu-dots-linear" className={`rotate-90 ${iconSizeClass}`} />
            </button>

            {mounted && isOpen &&
                createPortal(
                    <div
                        ref={menuRef}
                        role="menu"
                        style={{
                            position: "fixed",
                            top: coords.top,
                            left: coords.left,
                            width: MENU_WIDTH,
                            zIndex: 60,
                        }}
                        className="rounded-lg border border-gray-200 dark:border-borderDark bg-white dark:bg-bgDark-840 shadow-lg py-1"
                        onPointerDown={stop}
                        onClick={stop}
                    >
                        <button
                            type="button"
                            role="menuitem"
                            disabled={isLoading}
                            onClick={toggleArchive}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 dark:text-grayDark-200 hover:bg-gray-50 dark:hover:bg-white/10 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                        >
                            {isArchived ? (
                                <SolarIcon name="archive-up-linear" className="shrink-0 text-[16px]" />
                            ) : (
                                <SolarIcon name="archive-linear" className="shrink-0 text-[16px]" />
                            )}
                            <span>
                                {isArchived ? t("unarchive") : t("archive")}
                            </span>
                        </button>
                    </div>,
                    document.body,
                )}
        </>
    );
}
